/**
 * PCB Layout Node Action Creators
 *
 * This file contains action creators for PCB layout node operations.
 * These are pure functions that create action objects - they don't depend on Redux runtime.
 */

import {
    AnyPcbNode,
    PcbBakedNode,
    PcbNode,
    PcbNodeHelper,
    PcbNodeTypes,
    PcbRule,
    getAncestorUidByType,
    getStartEndPosition,
    vec2,
    createActionRecordAction,
    EditorModes,
    DocumentService,
} from "@buildwithflux/core";
import {
    AnyPcbBakedNode,
    IVector2,
    LayoutRules,
    PcbNodesMap,
    createRouteSegmentNode,
    INetsMap,
    IDocumentData,
    RuleKey,
    hasSomeLayoutRuleConfig,
    IPcbLayoutNodesRuleSetsMap,
    hasSomeLayoutRuleConfigInMap,
} from "@buildwithflux/models";
import {guid, humanizeLengthValue} from "@buildwithflux/shared";
import type {FluxServices} from "injection";
import {pick, merge, transform} from "lodash";

import type {DocumentStore} from "modules/document/state/document";
import {SnapToSegment} from "modules/stores/pcb/PcbEditorUiStore.types";
import {getAllBakedNodes} from "modules/stores/pcb/utils";

// Constants
export const pseudoStartTouchpointId = "pseudoStart";
export const pseudoEndTouchpointId = "pseudoEnd";

// ============================================================================
// addNodes
// ============================================================================

/**
 * Consider using dispatch(addNodesThunk(...)) instead of dispatching this action directly: it provides injected arguments
 */
export const addNodes = createActionRecordAction(
    "addNodes",
    (nodes: PcbNodesMap<AnyPcbNode>, editorMode: EditorModes) => {
        return {
            payload: {
                nodes,
                canUndo: true,
                inPcbEditor: editorMode === EditorModes.pcb,
                updatesDocumentTimestamp: true,
                shouldGenerateActionRecord: true,
            },
        };
    },
    "added PCB nodes",
);

export function addNodesThunk(
    services: Pick<FluxServices, "usePersistedDocumentUiStore" | "documentStore">,
    nodes: PcbNodesMap<AnyPcbNode>,
): void {
    const {editorMode} = services.usePersistedDocumentUiStore.getState();
    services.documentStore.getState().addNodes(nodes, editorMode === EditorModes.pcb);
}

// ============================================================================
// removeNodes
// ============================================================================

/**
 * This action can either entirely remove PCB nodes from their UID, or "toggle" them by using a rule set with an !important rule.
 * If you want to toggle the PCB node, pass them as baked nodes in the `toggleBakedNodes` and the action will create/remove a rule set for it.
 */
export const removeNodes = createActionRecordAction(
    "removeNodes",
    (
        removeNodeUids: string[],
        toggleBakedNodes: AnyPcbBakedNode[] = [],
        updatedNodesRulesSetsMap: IPcbLayoutNodesRuleSetsMap = {},
    ) => {
        return {
            payload: {
                removeNodeUids,
                toggleBakedNodes,
                updatedNodesRulesSetsMap,
                canUndo: true,
                updatesDocumentTimestamp: true,
                shouldGenerateActionRecord: true,

                hasInheritedRules: false,
                hasInheritedCalculations: false,
                requiresDescendantRebake: false,
                requiresNetDescendantRebake: false,
            },
        };
    },
    "removed PCB layout nodes",
);

// ============================================================================
// setNodeLabel
// ============================================================================

export const setNodeLabel = createActionRecordAction(
    "setNodeLabel",
    (nodeUid: string, label: string, canUndo = true) => {
        return {
            payload: {
                nodeUid,
                label,
                canUndo: canUndo,
                updatesDocumentTimestamp: true,
                shouldGenerateActionRecord: true,
                // QUESTION: why flags all true for a label change? because of
                // possible interaction with a global rule that selects on the
                // node label?
                hasInheritedRules: true,
                hasInheritedCalculations: true,
                requiresDescendantRebake: true,
                requiresNetDescendantRebake: true,
            },
        };
    },
    "updated PCB node label",
);

// ============================================================================
// removeNodeRules
// ============================================================================

export const removeNodeRules = createActionRecordAction(
    "removeNodeRules",
    (nodeId: string, ruleKeys: RuleKey[]) => {
        const hasInheritedRules = hasSomeLayoutRuleConfig(ruleKeys, "allChildNodesWillInheritThisRule");
        const hasInheritedCalculations = hasSomeLayoutRuleConfig(ruleKeys, "affectsDescendantCalculations");
        const requiresDescendantRebake = hasSomeLayoutRuleConfig(ruleKeys, "requiresDescendantRebake");
        const requiresNetDescendantRebake = hasSomeLayoutRuleConfig(ruleKeys, "requiresNetDescendantRebake");

        return {
            payload: {
                nodeId,
                ruleKeys,
                canUndo: true,
                updatesDocumentTimestamp: true,
                shouldGenerateActionRecord: true,

                hasInheritedRules,
                hasInheritedCalculations,
                requiresDescendantRebake,
                requiresNetDescendantRebake,
            },
        };
    },
    "remove object-specific pcb layout rules",
);

// ============================================================================
// toggleNodeSpecificRule
// ============================================================================

export const toggleNodeSpecificRule = createActionRecordAction(
    "toggleNodeSpecificRule",
    (ruleId: string, nodeId: string, toggleState: boolean, latestDocumentState: IDocumentData | null) => {
        const node = latestDocumentState!.pcbLayoutNodes[nodeId]!;
        const rule = Object.values(node.pcbNodeRuleSet).find((rule) => rule.uid === ruleId);
        if (!rule) throw new Error("Cannot find the rule to be toggled");

        const hasInheritedRules = LayoutRules[rule.key].allChildNodesWillInheritThisRule;
        const hasInheritedCalculations = LayoutRules[rule.key].affectsDescendantCalculations ?? false;
        const requiresDescendantRebake = LayoutRules[rule.key].requiresDescendantRebake ?? false;
        const requiresNetDescendantRebake = LayoutRules[rule.key].requiresNetDescendantRebake ?? false;
        return {
            payload: {
                nodeId,
                ruleId,
                toggleState,
                canUndo: true,
                updatesDocumentTimestamp: true,
                shouldGenerateActionRecord: true,
                hasInheritedRules,
                hasInheritedCalculations,
                requiresDescendantRebake,
                requiresNetDescendantRebake,
            },
        };
    },
    "toggles an object-specific layout rule",
);

export function toggleNodeSpecificRuleThunk(
    services: Pick<FluxServices, "documentStore" | "documentStore">,
    ruleId: string,
    nodeId: string,
    toggleState: boolean,
): void {
    services.documentStore.getState().toggleNodeSpecificRule(ruleId, nodeId, toggleState);
}

// ============================================================================
// updateRouteSegments
// ============================================================================

export const updateRouteSegments = createActionRecordAction(
    // QUESTION: why is this mixed plural and singular?
    "updateRouteSegment",
    (updates: {[uid: string]: PcbNode<PcbNodeTypes.routeSegment>}) => {
        return {
            payload: {
                updates,
                canUndo: true,
                updatesDocumentTimestamp: true,
                shouldGenerateActionRecord: true,
                // NOTE: assume the worst
                hasInheritedRules: true,
                hasInheritedCalculations: true,
                requiresDescendantRebake: true,
                requiresNetDescendantRebake: true,
            },
        };
    },
    "updated route segment",
);

// ============================================================================
// addRouteSegment
// ============================================================================

export interface IAddRouteSegmentParams {
    /**
     * Id of the start vertex
     * @deprecated
     */
    startId: string;
    /**
     * Id of the end vertex
     * @deprecated
     */
    endId: string;
    /**
     * Segments we are adding
     */
    // TODO: Interface for segment??
    segments: Array<{start: IVector2; end: IVector2}>;
    /**
     * uid of the parent node holding these segments
     */
    parentNetId: string;
    /**
     * Used to calculate relative positions
     */
    parentNetBakedNode: PcbBakedNode<PcbNodeTypes.net>;
    /**
     * Layer of new segments
     */
    layer: string;

    /**
     * Custom trace width of new segments
     */
    overrideTraceWidth?: {
        humanized: string;
        value: number;
    };

    /**
     * Via node that we might be placing during routing
     */
    via?: PcbNode<PcbNodeTypes.smartVia | PcbNodeTypes.via>;
    snapToSegment?: SnapToSegment | null;
    routeFromSegment?: SnapToSegment | null;
    /**
     * Once the route segments are instanciated (before adding them), the function sets this value.
     * This pattern is necessary to avoid waiting for the function to return (async).
     */
    addedUidsRef?: {current: string[]};
}

export interface AddRouteSegmentTraceWidthData {
    projectedTraceWidth: number;
    selectedTraceWidth: number;
    projectedHumanizedTraceWidth?: string;
    selectedHumanizedTraceWidth?: string;
    preventTraceWidthRuleOnTrace?: true;
}

// this deals with the special case of routing to / from an existing segment
function splitExistingSegment(
    segmentToSplit: SnapToSegment,
    variant: "start" | "end",
    segments: Array<{start: IVector2; end: IVector2}>,
    pcbLayoutNodes: PcbNodesMap<AnyPcbBakedNode>,
    startId: string,
    endId: string,
    layer: string,
    parentNetId: string,
    parentNetBakedNode: PcbBakedNode<PcbNodeTypes.net>,
) {
    const segment = segments.at(variant === "start" ? 0 : -1);
    if (!segment) {
        throw new Error(`Expected segment at '${variant}' to be defined`);
    }

    const segmentSplitTarget = pcbLayoutNodes[segmentToSplit.id];
    if (!segmentSplitTarget) {
        throw new Error(`Expected split target segment to be defined`);
    }

    const existingStart = PcbNodeHelper.scenePositionToNodeSpace(segmentToSplit.start, parentNetBakedNode);
    const existingEnd = PcbNodeHelper.scenePositionToNodeSpace(segmentToSplit.end, parentNetBakedNode);

    const vertex = segment[variant];
    const intersectionPoint = PcbNodeHelper.scenePositionToNodeSpace(vertex, parentNetBakedNode);

    const firstSegmentToAdd = createRouteSegmentNode(
        segmentToSplit.startId,
        variant === "start" ? startId : endId,
        {x: existingStart.x, y: existingStart.y},
        intersectionPoint,
        parentNetId,
        layer,
        (segmentSplitTarget.pcbNodeRuleSet.traceWidth?.value as string) ||
            (segmentSplitTarget.pcbNodeRuleSet.sizeX?.value as string),
        undefined,
        parentNetBakedNode.bakedRules.layer,
    );
    const secondSegmentToAdd = createRouteSegmentNode(
        segmentToSplit.endId,
        endId,
        {x: existingEnd.x, y: existingEnd.y},
        intersectionPoint,
        parentNetId,
        layer,
        (segmentSplitTarget.pcbNodeRuleSet.traceWidth?.value as string) ||
            (segmentSplitTarget.pcbNodeRuleSet.sizeX?.value as string),
        undefined,
        parentNetBakedNode.bakedRules.layer,
    );

    const nodesToAdd: AnyPcbNode[] = [];
    if (firstSegmentToAdd) {
        nodesToAdd.push(firstSegmentToAdd);
    }
    if (secondSegmentToAdd) {
        nodesToAdd.push(secondSegmentToAdd);
    }

    return {
        nodesToAdd,
        nodesToDelete: [segmentSplitTarget],
    };
}

function getNodesToAddFromSegments(
    segments: Array<{start: IVector2; end: IVector2}>,
    startId: string,
    endId: string,
    layer: string,
    parentNetId: string,
    parentNetBakedNode: PcbBakedNode<PcbNodeTypes.net>,
    projectedTraceWidth?: {
        value: number;
        humanized: string;
    },
) {
    const nodesToAdd: AnyPcbNode[] = [];
    let vertexId = startId;
    function addBaseSegment(segment: {start: IVector2; end: IVector2}, idx: number) {
        const isEnd = idx === segments.length - 1;
        const currentVertexId = guid();
        const absStart = PcbNodeHelper.scenePositionToNodeSpace(segment.start, parentNetBakedNode);
        const absEnd = PcbNodeHelper.scenePositionToNodeSpace(segment.end, parentNetBakedNode);
        const segmentEndVertex: IVector2 = absEnd;

        const normalized = normalizeCenter(absStart, segmentEndVertex);
        const humanizedCenter = [normalized.center.x, normalized.center.y]
            .map((v) => humanizeLengthValue(v, parentNetBakedNode.bakedRules.unit))
            .join(" ");

        const newSegment = createRouteSegmentNode(
            vertexId,
            isEnd ? endId : currentVertexId,
            normalized.start,
            normalized.end,
            parentNetId,
            layer,
            projectedTraceWidth?.humanized,
            humanizedCenter,
            parentNetBakedNode.bakedRules.layer,
        );

        if (newSegment) {
            nodesToAdd.push(newSegment);
        }
        vertexId = currentVertexId;
    }
    // eslint-disable-next-line @buildwithflux/no-array-foreach
    segments.forEach(addBaseSegment);

    return nodesToAdd;
}

function normalizeCenter(start: IVector2, end: IVector2) {
    const centerX = (start.x + end.x) / 2;
    const centerY = (start.y + end.y) / 2;
    return {
        center: {x: centerX, y: centerY},
        start: {x: start.x - centerX, y: start.y - centerY},
        end: {x: end.x - centerX, y: end.y - centerY},
    };
}

/**
 * Gets the current positions of elements to fix in place against
 * autopositioning when a trace is connected to them.
 *
 * See evaluateVector3 in PcbLayoutRuleParser.
 */
export function getUpdatedElementNodePositions(
    padUids: string[],
    pcbLayoutNodes: PcbNodesMap<AnyPcbBakedNode>,
): Record<string, PcbRule> {
    const padNodes = Object.values(pick(pcbLayoutNodes, padUids));
    const elementNodeUids = padNodes
        .map((padNode) => getAncestorUidByType(pcbLayoutNodes, padNode.uid, PcbNodeTypes.element))
        .filter(Boolean);
    return Object.fromEntries(
        elementNodeUids
            .map((nodeUid) => {
                const bakedElementNode = PcbNodeHelper.getNodeOfType(pcbLayoutNodes, nodeUid, PcbNodeTypes.element);
                const bakedRules = bakedElementNode?.bakedRules;
                if (bakedRules && (bakedRules.position.autoX || bakedRules.position.autoY)) {
                    const xPositionValue = humanizeLengthValue(bakedRules.position.x, bakedRules.unit);
                    const yPositionValue = humanizeLengthValue(bakedRules.position.y, bakedRules.unit);
                    const position: PcbRule = {
                        uid: "position",
                        key: "position",
                        value: `${xPositionValue} ${yPositionValue}`,
                        disabled: false,
                    };

                    return [nodeUid, position];
                }
                return [nodeUid, undefined];
            })
            .filter(([uid, pos]) => !!uid && !!pos) as [string, PcbRule][],
    );
}

export function addRouteSegmentThunk(
    services: Pick<FluxServices, "documentStore" | "documentService" | "documentStore">,
    params: IAddRouteSegmentParams,
): void {
    // Get document from Zustand store instead of Redux state
    const document = services.documentStore.getState().document;
    if (!document) {
        throw new Error("Document is not defined in the state");
    }

    const pcbLayoutNodes = getAllBakedNodes(services.documentService);

    const action = addRouteSegment(params, pcbLayoutNodes);
    services.documentStore
        .getState()
        .addRouteSegment(action.payload.addedNodes, action.payload.deletedNodes, action.payload.updatedNodePositions);
}

/**
 * NOTE: see also commitMultiRoutingUpdate
 */
export const addRouteSegment = createActionRecordAction(
    "addRouteSegment",
    (params: IAddRouteSegmentParams, pcbLayoutNodes: PcbNodesMap<AnyPcbBakedNode>) => {
        const {
            startId,
            endId,
            segments,
            parentNetId,
            layer,
            via,
            snapToSegment,
            routeFromSegment,
            addedUidsRef,
            parentNetBakedNode,
            overrideTraceWidth,
        } = params;

        const nodesToAdd: AnyPcbNode[] = [];
        const nodesToDelete: AnyPcbNode[] = [];

        const finalStartId = startId === pseudoStartTouchpointId ? guid() : startId;
        const finalEndId = endId === pseudoEndTouchpointId ? guid() : endId;

        nodesToAdd.push(
            ...getNodesToAddFromSegments(
                segments,
                finalStartId,
                finalEndId,
                layer,
                parentNetId,
                parentNetBakedNode,
                overrideTraceWidth,
            ),
        );

        if (snapToSegment) {
            // i.e. when we have ended routing by clicking on an existing trace
            const splitExistingSegmentGoods = splitExistingSegment(
                snapToSegment,
                "end",
                segments,
                pcbLayoutNodes,
                finalStartId,
                finalEndId,
                layer,
                parentNetId,
                parentNetBakedNode,
            );
            nodesToAdd.push(...splitExistingSegmentGoods.nodesToAdd);
            nodesToDelete.push(...splitExistingSegmentGoods.nodesToDelete);
        }

        if (routeFromSegment) {
            // i.e. when we have started routing by double-clicking on an existing trace
            const splitExistingSegmentGoods = splitExistingSegment(
                routeFromSegment,
                "start",
                segments,
                pcbLayoutNodes,
                finalStartId,
                finalEndId,
                layer,
                parentNetId,
                parentNetBakedNode,
            );
            nodesToAdd.push(...splitExistingSegmentGoods.nodesToAdd);
            nodesToDelete.push(...splitExistingSegmentGoods.nodesToDelete);
        }

        // When we're not routing from, or two, a midpoint generated along an existing route segment (eg, to/from a touchpoint
        // that's inserted after double click on an existing route segment), when we'll check to see if routing is going to or
        // from a route touchpoint that's repositioned at the transition between (a possible) curve and straight-line portion of
        // a trace
        if (!snapToSegment && !routeFromSegment) {
            const toSegmentId = endId.replace(/_end|_start$/, "");
            const toSegment = pcbLayoutNodes[toSegmentId];

            const fromSegmentId = startId.replace(/_end|_start$/, "");
            const fromSegment = pcbLayoutNodes[fromSegmentId];

            if (toSegment?.type === PcbNodeTypes.routeSegment) {
                const [startPosition, endPosition] = getStartEndPosition(toSegment);

                const jointPosition = segments.at(-1)?.end;
                if (!jointPosition) {
                    throw new Error("Curve trace joint position not defined");
                }

                const start = PcbNodeHelper.scenePositionToNodeSpace(startPosition, parentNetBakedNode);
                const end = PcbNodeHelper.scenePositionToNodeSpace(endPosition, parentNetBakedNode);
                const joint = PcbNodeHelper.scenePositionToNodeSpace(jointPosition, parentNetBakedNode);
                const jointId = guid();

                // Only insert non-zero length segments to ensure curvature is present on inserted/adjoined segments
                // If a zero-length segment is present it prevents curvature between itself and adjoined traces being possible
                if (vec2.distance(start, joint) > 0) {
                    nodesToAdd.push(
                        createRouteSegmentNode(
                            startId,
                            jointId,
                            {x: start.x, y: start.y},
                            joint,
                            parentNetId,
                            layer,
                            (toSegment.pcbNodeRuleSet.traceWidth?.value as string) ||
                                (toSegment.pcbNodeRuleSet.sizeX?.value as string),
                            undefined,
                            parentNetBakedNode.bakedRules.layer,
                        ),
                    );
                }

                // Only insert non-zero length segments to ensure curvature is present on inserted/adjoined segments
                // If a zero-length segment is present it prevents curvature between itself and adjoined traces being possible
                if (vec2.distance(joint, end) > 0) {
                    nodesToAdd.push(
                        createRouteSegmentNode(
                            jointId,
                            endId,
                            joint,
                            {x: end.x, y: end.y},
                            parentNetId,
                            layer,
                            (toSegment.pcbNodeRuleSet.traceWidth?.value as string) ||
                                (toSegment.pcbNodeRuleSet.sizeX?.value as string),
                            undefined,
                            parentNetBakedNode.bakedRules.layer,
                        ),
                    );
                }

                nodesToDelete.push(toSegment);
            }

            if (fromSegment?.type === PcbNodeTypes.routeSegment) {
                const [startPosition, endPosition] = getStartEndPosition(fromSegment);

                const jointPosition = segments.at(0)?.start;
                if (!jointPosition) {
                    throw new Error("Curve trace joint position not defined");
                }

                const start = PcbNodeHelper.scenePositionToNodeSpace(startPosition, parentNetBakedNode);
                const end = PcbNodeHelper.scenePositionToNodeSpace(endPosition, parentNetBakedNode);
                const joint = PcbNodeHelper.scenePositionToNodeSpace(jointPosition, parentNetBakedNode);
                const jointId = guid();

                // Only insert non-zero length segments to ensure curvature is present on inserted/adjoined segments
                // If a zero-length segment is present it prevents curvature between itself and adjoined traces being possible
                if (vec2.distance(joint, start) > 0) {
                    nodesToAdd.push(
                        createRouteSegmentNode(
                            startId,
                            jointId,
                            {x: start.x, y: start.y},
                            joint,
                            parentNetId,
                            layer,
                            (fromSegment.pcbNodeRuleSet.traceWidth?.value as string) ||
                                (fromSegment.pcbNodeRuleSet.sizeX?.value as string),
                            undefined,
                            parentNetBakedNode.bakedRules.layer,
                        ),
                    );
                }

                // Only insert non-zero length segments to ensure curvature is present on inserted/adjoined segments
                // If a zero-length segment is present it prevents curvature between itself and adjoined traces being possible
                if (vec2.distance(joint, end) > 0) {
                    nodesToAdd.push(
                        createRouteSegmentNode(
                            jointId,
                            endId,
                            joint,
                            {x: end.x, y: end.y},
                            parentNetId,
                            layer,
                            (fromSegment.pcbNodeRuleSet.traceWidth?.value as string) ||
                                (fromSegment.pcbNodeRuleSet.sizeX?.value as string),
                            undefined,
                            parentNetBakedNode.bakedRules.layer,
                        ),
                    );
                }

                nodesToDelete.push(fromSegment);
            }
        }

        // Place a via if in the middle of routing
        if (via) {
            nodesToAdd.push(via);
        }

        if (addedUidsRef) {
            addedUidsRef.current = nodesToAdd.map((n) => n.uid);
        }

        const updatedNodePositions = getUpdatedElementNodePositions([startId, endId], pcbLayoutNodes);

        return {
            payload: {
                addedNodes: nodesToAdd,
                deletedNodes: nodesToDelete,
                updatedNodePositions,
                canUndo: true,
                updatesDocumentTimestamp: true,
                shouldGenerateActionRecord: true,
            },
        };
    },
    "updated PCB net nodes",
);

// ============================================================================
// addPolygon
// ============================================================================

export const addPolygon = createActionRecordAction(
    "addPolygon",
    (
        node: PcbNode<PcbNodeTypes.polygon>,
        editorMode: EditorModes,
        documentNets: INetsMap,
        pcbLayoutNodes: PcbNodesMap<AnyPcbBakedNode>,
    ) => {
        // When adding a polygon we don't know what's going to be connected
        // To be safe, we fix the position of all pins in the net
        const nodeNet = node.parentUid;
        const allPinsInNet = Object.keys(documentNets[nodeNet]?.pins ?? {});
        const updatedNodePositions = getUpdatedElementNodePositions(allPinsInNet, pcbLayoutNodes);

        return {
            payload: {
                node,
                updatedNodePositions,
                canUndo: true,
                inPcbEditor: editorMode === EditorModes.pcb,
                updatesDocumentTimestamp: true,
                shouldGenerateActionRecord: true,
            },
        };
    },
    "added PCB polygon",
);

export function addPolygonThunk(
    services: Pick<FluxServices, "documentStore" | "usePersistedDocumentUiStore" | "documentService" | "documentStore">,
    node: PcbNode<PcbNodeTypes.polygon>,
): void {
    // Get document from Zustand store instead of Redux state
    const document = services.documentStore.getState().document;
    if (!document) {
        throw new Error("Document not found in state");
    }

    const {editorMode} = services.usePersistedDocumentUiStore.getState();
    const pcbLayoutNodes = getAllBakedNodes(services.documentService);
    const nodeNet = node.parentUid;
    const allPinsInNet = Object.keys(document.nets[nodeNet]?.pins ?? {});
    const updatedNodePositions = getUpdatedElementNodePositions(allPinsInNet, pcbLayoutNodes);
    services.documentStore.getState().addPolygon(node, updatedNodePositions, editorMode === EditorModes.pcb);
}

// ============================================================================
// commitMultiRoutingUpdate
// ============================================================================

type AddRouteSegmentActionParams = Parameters<typeof addRouteSegment>;

interface ICommitMultiRoutingUpdateParams {
    updatedNodesRulesSetsMap: IPcbLayoutNodesRuleSetsMap;
    addRouteSegmentParams: IAddRouteSegmentParams[];
    removeNodeUids?: string[];
}

/**
 * A temporary function that's basically used to acquire route segment uids (via addedUidsRef in addRouteSegment).
 *
 * This is a definiate hack - done to avoid a larger refactor, that'd likely involve splitting addRouteSegment(..)
 * into multiple functions.
 */
export function getRoutingUidsWithoutCommit_TEMP(
    params: ICommitMultiRoutingUpdateParams,
    documentService: DocumentService,
) {
    const pcbLayoutNodes = getAllBakedNodes(documentService);

    const addRouteSegmentParams = params.addRouteSegmentParams.map<AddRouteSegmentActionParams>((params) => [
        params,
        pcbLayoutNodes,
    ]);

    for (const params of addRouteSegmentParams) {
        addRouteSegment(...params);
    }
}

export function commitMultiRoutingUpdateThunk(
    services: {documentStore: DocumentStore; documentService: DocumentService},
    params: ICommitMultiRoutingUpdateParams,
): void {
    // Get document from Zustand store instead of Redux state
    const document = services.documentStore.getState().document;
    if (!document) {
        throw new Error("Document not found in state.");
    }

    const pcbLayoutNodes = getAllBakedNodes(services.documentService);

    const addRouteSegmentParams = params.addRouteSegmentParams.map<AddRouteSegmentActionParams>((params) => [
        params,
        pcbLayoutNodes,
    ]);

    const action = commitMultiRoutingUpdate(params, addRouteSegmentParams);
    services.documentStore
        .getState()
        .commitMultiRoutingUpdate(action.payload.addedNodes, action.payload.deletedNodes, action.payload.updatedNodes);
}

export const commitMultiRoutingUpdate = createActionRecordAction(
    "commitMultiRoutingUpdate",
    (params: ICommitMultiRoutingUpdateParams, addRouteSegmentParams: AddRouteSegmentActionParams[]) => {
        const hasInheritedRules = hasSomeLayoutRuleConfigInMap(
            params.updatedNodesRulesSetsMap,
            "allChildNodesWillInheritThisRule",
        );
        const hasInheritedCalculations = hasSomeLayoutRuleConfigInMap(
            params.updatedNodesRulesSetsMap,
            "affectsDescendantCalculations",
        );
        const requiresDescendantRebake = hasSomeLayoutRuleConfigInMap(
            params.updatedNodesRulesSetsMap,
            "requiresDescendantRebake",
        );
        const requiresNetDescendantRebake = hasSomeLayoutRuleConfigInMap(
            params.updatedNodesRulesSetsMap,
            "requiresNetDescendantRebake",
        );

        const addRouteSegmentResults = addRouteSegmentParams.map((params) => addRouteSegment(...params));

        // Flatten updatedNodePositions and convert to
        // IPcbLayoutNodesRuleSetsMap for merging with
        // params.updatedNodesRulesSetsMap
        const updatedNodePositions: Record<string, PcbRule> = merge(
            {},
            ...addRouteSegmentResults.map((n) => n.payload.updatedNodePositions),
        );
        const updatedNodePositionsRuleSetsMap = transform(
            updatedNodePositions,
            (result, position, nodeUid) => {
                result[nodeUid] = {nodeUid, updatedPcbLayoutRules: {position}};
            },
            {} as IPcbLayoutNodesRuleSetsMap,
        );

        return {
            payload: {
                addedNodes: addRouteSegmentResults.flatMap((n) => n.payload.addedNodes),
                deletedNodes: [
                    ...addRouteSegmentResults.flatMap((nf) => nf.payload.deletedNodes.map((n) => n.uid)),
                    ...(params.removeNodeUids ?? []),
                ],
                updatedNodes: merge(params.updatedNodesRulesSetsMap, updatedNodePositionsRuleSetsMap),
                canUndo: true,
                updatesDocumentTimestamp: true,
                shouldGenerateActionRecord: true,

                hasInheritedRules,
                hasInheritedCalculations,
                requiresDescendantRebake,
                requiresNetDescendantRebake,
            },
        };
    },
    "performed multi routing",
);
